import React, { useEffect, useMemo } from "react";
import Head from "react-helmet";
import {
  isExternalPrivacyPolicyUrl,
  resolvePrivacyPolicyUrl,
} from "./privacyPolicyLink";

const SILKTIDE_VERSION = "v2.0.0";
const SILKTIDE_CSS_ID = "silktide-consent-manager-css";
const SILKTIDE_SCRIPT_ID = "silktide-consent-manager-script";
const SILKTIDE_CDN_BASE = `https://cdn.jsdelivr.net/gh/silktide/consent-manager@${SILKTIDE_VERSION}`;
const SILKTIDE_CSS_URL = `${SILKTIDE_CDN_BASE}/silktide-consent-manager.css`;
const SILKTIDE_SCRIPT_URL = `${SILKTIDE_CDN_BASE}/silktide-consent-manager.js`;
const SILKTIDE_CSS_INTEGRITY =
  "sha384-IO1E/jCrQXyH5rwcI0SXP7OXw47JFqQNDQcKhbFvqnL2IunBxxwE2Ne5XyAmCqKs";
const SILKTIDE_SCRIPT_INTEGRITY =
  "sha384-j4NIMOecmtzMWe9GJADIIe5hTlHG63aiTQ/2XorW10RNyQJg+IU+xwFVDy45wBah";

// The cookie consent pop-up links to the cookie policy (hardcoded per client
// request), independent of the subdomain-level privacy policy URL used by the
// legal consent checkbox under the forms.
const COOKIE_POLICY_URL = "https://www.mediamixer.be/cookie-policy";

const defaultConsentTypes = [
  {
    id: "analytics",
    label: "Analytics",
    description:
      "These help us understand how visitors interact with the website.",
    defaultValue: false,
    gtag: "analytics_storage",
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "These are used to deliver personalized advertisements.",
    defaultValue: false,
    gtag: ["ad_storage", "ad_user_data", "ad_personalization"],
  },
  {
    id: "essential",
    label: "Essential",
    description:
      "These cookies are necessary for the website to function and cannot be switched off.",
    required: true,
    gtag: ["functionality_storage", "security_storage"],
  },
];

const defaultText = {
  prompt: {
    description:
      "We use cookies on our site to enhance your user experience, provide personalized content, and analyze our traffic.",
    privacyLinkText: "Cookie Policy",
    acceptAllButtonText: "Accept all",
    acceptAllButtonAccessibleLabel: "Accept all cookies",
    rejectNonEssentialButtonText: "Reject non-essential",
    rejectNonEssentialButtonAccessibleLabel: "Reject non-essential cookies",
    preferencesButtonText: "Preferences",
    preferencesButtonAccessibleLabel: "Open cookie preferences",
  },
  preferences: {
    title: "Cookie preferences",
    description:
      "We respect your right to privacy. You can choose not to allow some types of cookies. Your cookie preferences will apply across our website.",
    saveButtonText: "Save preferences",
    saveButtonAccessibleLabel: "Save cookie preferences",
  },
};

const getWindow = () => (typeof window === "undefined" ? null : window);

const escapeHtml = (value) =>
  `${value}`
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getPolicyLinksHtml = ({ privacyLinkText, privacyPolicyUrl }) => {
  const newTabAttributes = isExternalPrivacyPolicyUrl(privacyPolicyUrl)
    ? ' target="_blank" rel="noopener noreferrer"'
    : "";
  const links = [
    privacyLinkText &&
      `<a href="${escapeHtml(
        privacyPolicyUrl,
      )}"${newTabAttributes}>${escapeHtml(privacyLinkText)}</a>`,
  ].filter(Boolean);

  if (links.length === 0) {
    return "";
  }

  return `<br />${links.join(" &middot; ")}`;
};

const normalizeBackdrop = (backdrop) => {
  if (backdrop === true) {
    return { show: true };
  }

  return backdrop;
};

const loadSilktideScript = () =>
  new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SILKTIDE_SCRIPT_ID);
    const win = getWindow();

    if (win && win.silktideConsentManager) {
      resolve();
      return;
    }

    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SILKTIDE_SCRIPT_ID;
    script.src = SILKTIDE_SCRIPT_URL;
    script.integrity = SILKTIDE_SCRIPT_INTEGRITY;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", reject, { once: true });

    document.head.appendChild(script);
  });

const normalizeText = ({
  message,
  acceptButtonText,
  bannerText,
  bannerDescription,
  acceptAllButtonText,
  acceptAllButtonAccessibleLabel,
  rejectButtonText,
  rejectButtonAccessibleLabel,
  preferencesButtonText,
  preferencesButtonAccessibleLabel,
  privacyLinkText,
  privacyPolicyUrl,
  preferencesTitle,
  preferencesDescription,
  saveButtonText,
  saveButtonAccessibleLabel,
  text,
  translations,
}) => {
  const normalizedPrivacyLinkText =
    privacyLinkText ||
    (text && text.prompt && text.prompt.privacyLinkText) ||
    (translations &&
      translations.prompt &&
      translations.prompt.privacyLinkText) ||
    (translations &&
      translations.banner &&
      translations.banner.privacyLinkText) ||
    defaultText.prompt.privacyLinkText;
  const normalizedPrivacyPolicyUrl = resolvePrivacyPolicyUrl(
    privacyPolicyUrl ||
      (text && text.prompt && text.prompt.privacyPolicyUrl) ||
      (translations &&
        translations.prompt &&
        translations.prompt.privacyPolicyUrl) ||
      (translations &&
        translations.banner &&
        translations.banner.privacyPolicyUrl),
  );
  const description =
    bannerText ||
    bannerDescription ||
    message ||
    (text && text.prompt && text.prompt.description) ||
    (translations && translations.prompt && translations.prompt.description) ||
    (translations && translations.banner && translations.banner.description) ||
    defaultText.prompt.description;

  return {
    ...defaultText,
    ...text,
    ...translations,
    prompt: {
      ...defaultText.prompt,
      ...(text && text.prompt),
      ...(translations && translations.prompt),
      ...(translations && translations.banner),
      description: `${description}${getPolicyLinksHtml({
        privacyLinkText: normalizedPrivacyLinkText,
        privacyPolicyUrl: normalizedPrivacyPolicyUrl,
      })}`,
      privacyLinkText: normalizedPrivacyLinkText,
      privacyPolicyUrl: normalizedPrivacyPolicyUrl,
      acceptAllButtonText:
        acceptAllButtonText ||
        acceptButtonText ||
        (text && text.prompt && text.prompt.acceptAllButtonText) ||
        (translations &&
          translations.prompt &&
          translations.prompt.acceptAllButtonText) ||
        (translations &&
          translations.banner &&
          translations.banner.acceptAllButtonText) ||
        defaultText.prompt.acceptAllButtonText,
      acceptAllButtonAccessibleLabel:
        acceptAllButtonAccessibleLabel ||
        (text && text.prompt && text.prompt.acceptAllButtonAccessibleLabel) ||
        (translations &&
          translations.prompt &&
          translations.prompt.acceptAllButtonAccessibleLabel) ||
        (translations &&
          translations.banner &&
          translations.banner.acceptAllButtonAccessibleLabel) ||
        defaultText.prompt.acceptAllButtonAccessibleLabel,
      rejectNonEssentialButtonText:
        rejectButtonText ||
        (text && text.prompt && text.prompt.rejectNonEssentialButtonText) ||
        (translations &&
          translations.prompt &&
          translations.prompt.rejectNonEssentialButtonText) ||
        (translations &&
          translations.banner &&
          translations.banner.rejectNonEssentialButtonText) ||
        defaultText.prompt.rejectNonEssentialButtonText,
      rejectNonEssentialButtonAccessibleLabel:
        rejectButtonAccessibleLabel ||
        (text &&
          text.prompt &&
          text.prompt.rejectNonEssentialButtonAccessibleLabel) ||
        (translations &&
          translations.prompt &&
          translations.prompt.rejectNonEssentialButtonAccessibleLabel) ||
        (translations &&
          translations.banner &&
          translations.banner.rejectNonEssentialButtonAccessibleLabel) ||
        defaultText.prompt.rejectNonEssentialButtonAccessibleLabel,
      preferencesButtonText:
        preferencesButtonText ||
        (text && text.prompt && text.prompt.preferencesButtonText) ||
        (translations &&
          translations.prompt &&
          translations.prompt.preferencesButtonText) ||
        (translations &&
          translations.banner &&
          translations.banner.preferencesButtonText) ||
        defaultText.prompt.preferencesButtonText,
      preferencesButtonAccessibleLabel:
        preferencesButtonAccessibleLabel ||
        (text && text.prompt && text.prompt.preferencesButtonAccessibleLabel) ||
        (translations &&
          translations.prompt &&
          translations.prompt.preferencesButtonAccessibleLabel) ||
        (translations &&
          translations.banner &&
          translations.banner.preferencesButtonAccessibleLabel) ||
        defaultText.prompt.preferencesButtonAccessibleLabel,
    },
    preferences: {
      ...defaultText.preferences,
      ...(text && text.preferences),
      ...(translations && translations.preferences),
      title:
        preferencesTitle ||
        (text && text.preferences && text.preferences.title) ||
        (translations &&
          translations.preferences &&
          translations.preferences.title) ||
        defaultText.preferences.title,
      description:
        preferencesDescription ||
        (text && text.preferences && text.preferences.description) ||
        (translations &&
          translations.preferences &&
          translations.preferences.description) ||
        defaultText.preferences.description,
      saveButtonText:
        saveButtonText ||
        (text && text.preferences && text.preferences.saveButtonText) ||
        (translations &&
          translations.preferences &&
          translations.preferences.saveButtonText) ||
        defaultText.preferences.saveButtonText,
      saveButtonAccessibleLabel:
        saveButtonAccessibleLabel ||
        (text &&
          text.preferences &&
          text.preferences.saveButtonAccessibleLabel) ||
        (translations &&
          translations.preferences &&
          translations.preferences.saveButtonAccessibleLabel) ||
        defaultText.preferences.saveButtonAccessibleLabel,
    },
  };
};

const applyGoogleConsentDefaults = (consentTypes) => {
  const win = getWindow();

  if (!win) {
    return;
  }

  win.dataLayer = win.dataLayer || [];
  win.gtag =
    win.gtag ||
    function gtag(...args) {
      win.dataLayer.push(args);
    };

  const defaults = consentTypes.reduce((acc, consentType) => {
    const signals = Array.isArray(consentType.gtag)
      ? consentType.gtag
      : [consentType.gtag];

    signals.filter(Boolean).forEach((signal) => {
      acc[signal] =
        localStorage.getItem(`stcm.consent.${consentType.id}`) === "true"
          ? "granted"
          : "denied";
    });

    return acc;
  }, {});

  if (Object.keys(defaults).length > 0) {
    win.gtag("consent", "default", defaults);
  }
};

export const CookieConsent = ({
  consentTypes,
  cookieTypes,
  eventName,
  dataLayerEventName,
  backdrop,
  background,
  position = "bottomCenter",
  cookieIconPosition = "bottomLeft",
  primaryColor = "#533BE2",
  backgroundColor = "#FFFFFF",
  textColor = "#4B494B",
  message,
  acceptButtonText,
  bannerText,
  bannerDescription,
  acceptAllButtonText,
  acceptAllButtonAccessibleLabel,
  rejectButtonText,
  rejectButtonAccessibleLabel,
  preferencesButtonText,
  preferencesButtonAccessibleLabel,
  privacyLinkText,
  preferencesTitle,
  preferencesDescription,
  saveButtonText,
  saveButtonAccessibleLabel,
  text,
  translations = {},
}) => {
  const privacyPolicyUrl = COOKIE_POLICY_URL;
  const normalizedConsentTypes = useMemo(
    () => consentTypes || cookieTypes || defaultConsentTypes,
    [consentTypes, cookieTypes],
  );
  const managerText = useMemo(
    () =>
      normalizeText({
        message,
        acceptButtonText,
        bannerText,
        bannerDescription,
        acceptAllButtonText,
        acceptAllButtonAccessibleLabel,
        rejectButtonText,
        rejectButtonAccessibleLabel,
        preferencesButtonText,
        preferencesButtonAccessibleLabel,
        privacyLinkText,
        privacyPolicyUrl,
        preferencesTitle,
        preferencesDescription,
        saveButtonText,
        saveButtonAccessibleLabel,
        text,
        translations,
      }),
    [
      acceptAllButtonAccessibleLabel,
      acceptAllButtonText,
      acceptButtonText,
      bannerDescription,
      bannerText,
      message,
      preferencesButtonAccessibleLabel,
      preferencesButtonText,
      privacyLinkText,
      privacyPolicyUrl,
      preferencesDescription,
      preferencesTitle,
      rejectButtonAccessibleLabel,
      rejectButtonText,
      saveButtonAccessibleLabel,
      saveButtonText,
      text,
      translations,
    ],
  );
  const consentEventName = eventName || dataLayerEventName;
  const consentBackdrop = useMemo(
    () => normalizeBackdrop(backdrop || background),
    [backdrop, background],
  );

  useEffect(() => {
    const win = getWindow();

    if (!win) {
      return undefined;
    }

    let isActive = true;

    applyGoogleConsentDefaults(normalizedConsentTypes);

    loadSilktideScript()
      .then(() => {
        if (!isActive || !win.silktideConsentManager) {
          return;
        }

        win.silktideConsentManager.init({
          consentTypes: normalizedConsentTypes,
          ...(consentEventName && { eventName: consentEventName }),
          ...(consentBackdrop && { backdrop: consentBackdrop }),
          prompt: { position },
          icon: { position: cookieIconPosition },
          text: managerText,
        });
      })
      .catch((error) => {
        console.error("Silktide Consent Manager failed to load", error);
      });

    return () => {
      isActive = false;
    };
  }, [
    consentBackdrop,
    consentEventName,
    cookieIconPosition,
    managerText,
    normalizedConsentTypes,
    position,
  ]);

  return (
    <Head>
      <link
        rel="preconnect"
        href="https://cdn.jsdelivr.net"
        crossOrigin="anonymous"
      />
      <link
        rel="stylesheet"
        id={SILKTIDE_CSS_ID}
        href={SILKTIDE_CSS_URL}
        integrity={SILKTIDE_CSS_INTEGRITY}
        crossOrigin="anonymous"
      />
      <style>
        {`
          #stcm-wrapper {
            --primaryColor: ${primaryColor ?? "#000000"};
            --backgroundColor: ${backgroundColor ?? "#FFFFFF"};
            --textColor: ${textColor ?? "#4B494B"};
          }

          #stcm-button,
          #stcm-icon {
            display: none !important;
          }
        `}
      </style>
    </Head>
  );
};
